import { Combobox } from '@/components/ui/combobox/combobox';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import useUsers from '@/hooks/use-users';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import type { Deal } from '@/models/registry';
import { dealStore } from '@/models/stores';
import { useMutation } from '@tanstack/react-query';
import { noop } from 'lodash';
import { useMemo } from 'react';
import { transformToFinanceManagerOptions } from './finance-manager-selector';

export type FinanceManagerComboboxProps = {
  id: Deal['id'];
  value: Deal['finance_manager'];

  afterOnChange?: () => void;
  onSuccess?: (
    data: Deal,
    variables: { financeManager: Deal['finance_manager'] },
  ) => void;
  onError?: (
    error: Error,
    variables: { financeManager: Deal['finance_manager'] },
  ) => void;
};

export function FinanceManagerCombobox({
  id,
  value,
  afterOnChange,
  onSuccess,
  onError,
}: FinanceManagerComboboxProps) {
  const { financeManagers } = useUsers();

  const updateFinanceManagerMutation = useMutation({
    onMutate: async ({ financeManager }) => {
      if (!financeManager) return;

      dealStore.updateFinanceManager({
        id,
        financeManager,
      });
    },
    mutationFn: async ({
      financeManager,
    }: {
      financeManager: Deal['finance_manager'];
    }) => {
      if (!financeManager) return;

      const response = await pdkAxios
        .post(`/deals/${id}/finance-manager/${financeManager.id}`, {
          finance_manager_id: financeManager.id.toString(),
        })
        .catch((e) => {
          console.error('Error updating finance manager', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: (data, variables) => {
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      onError?.(error, variables);

      console.error('Error updating finance manager', error, variables);
    },
    onSettled: () => {
      // DEV NOTE: invalidateQueries is cause a flicker bug
      // because the useQuery hook in useDeal re runs and gives back an old value
      // It may not even be refetching ...
      // queryClient.invalidateQueries({ queryKey: ["deal", id] });
    },
  });

  const deleteFinanceManagerMutation = useMutation({
    onMutate: async () => {
      dealStore.updateFinanceManager({
        id,
        financeManager: null,
      });
    },
    mutationFn: async ({
      currentFinanceManager,
    }: {
      currentFinanceManager: Deal['finance_manager'];
    }) => {
      const response = await pdkAxios
        .post(
          `/deals/${id}/finance-manager/${currentFinanceManager?.id}/delete`,
        )
        .catch((e) => {
          console.error('Error deleting finance manager', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {},
    onError: (error, variables) => {
      console.error('Error deleting finance manager', error, variables);
    },
    onSettled: () => {
      // DEV NOTE: invalidateQueries is cause a flicker bug
      // because the useQuery hook in useDeal re runs and gives back an old value
      // queryClient.invalidateQueries({ queryKey: ["deal", id] });
    },
  });

  const financeManagerOptions = useMemo(() => {
    return transformToFinanceManagerOptions(financeManagers);
  }, [financeManagers]);

  const handleFinanceManagerChange = (newFinanceManagerId: string) => {
    if (newFinanceManagerId === 'no-finance-manager') {
      deleteFinanceManagerMutation.mutate({
        currentFinanceManager: value,
      });

      afterOnChange?.();
      return;
    }

    const newFinanceManager = financeManagers.find(
      (option) => option.id === newFinanceManagerId,
    );

    if (!newFinanceManager) return;

    updateFinanceManagerMutation.mutate({
      financeManager: newFinanceManager,
    });

    afterOnChange?.();
  };

  return (
    <Combobox
      endAdornment={
        <KeyboardKeyIcon>{HOTKEYS.DEAL_FINANCE_MANAGER}</KeyboardKeyIcon>
      }
      placeholder="Set finance manager..."
      onChange={handleFinanceManagerChange}
      emptyMessage="No finance managers found"
      options={financeManagerOptions}
      setOpen={noop}
      onKeyDown={(e) => {
        const keyPressed = e.key;
        const option = financeManagerOptions.find(
          (option) => option.value === keyPressed,
        );
        if (option) {
          handleFinanceManagerChange(option.value);
        }
      }}
    ></Combobox>
  );
}
